
/**
 * Module dependencies.
 */

import { NavThemeProps, borderOpacity, navTransition } from './nav-theme';
import { color, media, units } from 'src/styles/utils';
import { isInternalRoute } from './nav-element';
import { switchProp, theme } from 'styled-tools';
import { useTranslate } from 'src/core/utils/translator';
import LanguageDropdown from './language-dropdown';
import NavTertiaryLink from './nav-tertiary-link';
import React, { ReactElement } from 'react';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import styled, { css } from 'styled-components';
import useTopBarMenu from 'src/api/app/top-bar-menu/use-top-bar-menu';

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div<NavThemeProps>`
  align-items: center;
  backdrop-filter: blur(25px);
  border-bottom: 1px solid;
  display: none;
  height: ${theme('dimensions.preNavbarHeight')}px;
  justify-content: flex-end;
  padding: 0 ${units(7)};
  position: relative;
  transition: ${navTransition};
  transition-property: background-color, border-color;
  z-index: 1;

  ${media.min('md')`
    display: flex;
  `}

  /*
   * In the dark theme the white comes from the navbar itself. Painting a
   * second white layer here would make this strip reach full white sooner
   * than the row below it.
   */
  ${switchProp('colorTheme', {
    dark: css`
      background-color: transparent;
      border-bottom-color: ${color('beige100')};
    `,
    light: css`
      background-color: ${color.transparentize('white', 0.05)};
      border-bottom-color: ${color.transparentize('neutralDarkest', borderOpacity)};
    `
  })}
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: ${units(2)};
`;

/**
 * `Links` styled component.
 */

const Links = styled.div`
  align-items: center;
  display: flex;
  gap: ${units(2)};
`;

/**
 * `PreNavbar` component.
 *
 * The back-office managed bar sitting on top of the navbar.
 */

const PreNavbar = ({ colorTheme }: NavThemeProps): ReactElement => {
  const { locale } = useTranslate();
  const { data } = useTopBarMenu();
  const links = isArray(data) ? data : [];

  return (
    <Wrapper colorTheme={colorTheme}>
      <Actions>
        <Links>
          {map(links, ({ label, link }) => link && (
            <NavTertiaryLink
              colorTheme={colorTheme}
              href={link}
              key={label}
              {...isInternalRoute(link) && { locale }}
            >
              {label}
            </NavTertiaryLink>
          ))}
        </Links>

        <LanguageDropdown colorTheme={colorTheme} />
      </Actions>
    </Wrapper>
  );
};

/**
 * Export `PreNavbar` component.
 */

export default PreNavbar;
